import { css } from '@linaria/core'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { HoverTooltip } from '#components/HoverTooltip'
import { Icon } from '#components/Icon'
import { toaster } from '#features/Toasts/toaster'
import FlatLogoIcon from '#icons/man-logo-flat.svg'
import { fragments, style } from '#styles'
import { PageHeaderSlots } from './PageHeaderSlots'
import { usePageHeaderContext } from './usePageHeaderContext'

const navClass = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  height: var(--header-height, 72px);
  position: sticky;
  top: 0;
  background: ${style.themeColors.backgroundDefault};
  z-index: 20000;    


  .content-area {
    .nav-area {
      display: flex;
      padding-left: 12px;
      padding-right: 12px;
      // looks like a bottom border, but isn't one.
      // this pushes no border space, such as elements can keep in line at the bottom
      box-shadow: inset 0 -2px 0 ${style.themeColors.lineDefault};
    }
  }

  > ._logo-container {
    width: auto;
    height: 72px;
    overflow: hidden;
    padding-left: 24px;
    padding-right: 12px;
    display: flex;
    align-items: center;
    border-bottom: 2px solid ${style.themeColors.lineDefault};

    > .logo-icon {
      height: 42px;
      width: 72px;
    }
  }

  @media print {
    display: none;
  }
`

const headersClass = css`
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0px;

  // disallow pushing the layout out of bounds
  overflow: hidden;

  .title-area {
    padding-top: 12px;
    padding-left: 24px;
    display: flex;
    gap: 8px;

    > h1,
    > span {
      ${fragments.textStyle.homelink.s}
      color: ${style.themeColors.textDimmed};
      line-height: 100%;
    }

    > h1 {
      margin: 0;
    }

    > span {
      opacity: 0.5;
    }
  }
`

const navEntriesGroupClass = css`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-end;
  flex-wrap: nowrap;
`

export const PageHeader: React.FC<{ appName: string }> = ({ appName }) => {
  const { setBaseRoot } = usePageHeaderContext()
  const { t } = useTranslation('shared')
  return (
    <nav className={navClass}>
      <div className="_logo-container">
        <Icon title={appName ?? t('app.name-missing', 'App Name Missing')} className="logo-icon" iconUrl={FlatLogoIcon} />
      </div>
      <div className={`content-area ${headersClass}`}>
        <div className="title-area">
          <h1>{appName ?? t('app.name-missing', 'App Name Missing')}</h1>
          <HoverTooltip.span
            onClick={() => {
              void navigator.clipboard.writeText(`v${VITE_APP_VERSION} sha:${VITE_COMMIT_SHA} at ${VITE_BUILD_TIME}`)
              toaster.add({ title: t('common.toasts.copied-to-clipboard', 'Copied to clipboard') })
            }}
            title={`v${VITE_APP_VERSION} sha:${VITE_COMMIT_SHA} at ${VITE_BUILD_TIME}`}
          >
            v{VITE_APP_VERSION}
          </HoverTooltip.span>
        </div>
        <div className={`nav-area ${navEntriesGroupClass}`} ref={setBaseRoot}>
          <PageHeaderSlots />
        </div>
      </div>
    </nav>
  )
}
